import {useAuth} from '@saltus/auth';
import React, {useEffect, useMemo} from 'react';
import {
  Routes,
  BrowserRouter as Router,
  Route,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import './App.css';
import parser from 'tld-extract';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterMoment} from '@mui/x-date-pickers-pro/AdapterMoment';
import NotificationsProvider from './components/NotificationsProvider/NotificationsProvider';
import SaltusThemeProvider from './contexts/ThemeProvider';
import AppAuth from './components/AppAuth/AppAuth';
import debug from './services/debug';
import EnforceMfa from './components/Mfa/EnforceMfaDjango';
import MfaEnrolmentRoutes from './components/Mfa/MfaEnrolmentRoutes';
import {getURLPriorToLogin, useURLPriorToLogin} from './hooks/useURLPriorToLogin';
import {MainRoutes} from './Routes';
import {SessionExpiredDialog} from './apollo';
import useDjango from './hooks/useDjango';
import {OfflinePage} from './pages/OfflinePage';
import Loading from './components/Loading/Loading';

export function ProtectedApp() {
  // router must be around notifications for links in snackbar notifications
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Routes>
        <Route path="/2fa-enrolment/*" element={<MfaEnrolmentRoutes />} />
        <Route
          path="/*"
          element={
            <EnforceMfa>
              <NotificationsProvider
                anchorOrigin={{
                  horizontal: 'center',
                  vertical: 'bottom',
                }}
              >
                {/* <DjangoProvider> */}
                <MainRoutes />
                {/* </DjangoProvider> */}
              </NotificationsProvider>
            </EnforceMfa>
          }
        />
      </Routes>
    </LocalizationProvider>
  );
}

export function OfflineApp({isOnline}) {
  const navigate = useNavigate();
  useEffect(() => {
    if (isOnline) {
      return;
    }
    navigate({pathname: '/offline'});
  }, [isOnline]);

  return (
    <Routes>
      <Route
        path="/offline"
        element={<OfflinePage text="You have been logged out due to inactivity" />}
      />
      {/* if online, then authenticate the user */}
      <Route path="/" element={<AppAuth />} />
    </Routes>
  );
}

function App() {
  const {saveURLPriorToLogin} = useURLPriorToLogin();
  const {authStatus, loading: authLoading} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {error, loading: djangoLoading} = useDjango();

  const tld =
    window.location.hostname === 'localhost'
      ? null
      : parser(window.location.href, {allowUnknownTLD: true});
  let cookieDomain = tld ? tld.domain : 'localhost';
  if (cookieDomain === 'saltusdev.co.uk') {
    cookieDomain = window.location.hostname;
  }

  debug('cookieDomain', cookieDomain);

  useEffect(() => {
    saveURLPriorToLogin();
  }, [location]);

  useEffect(() => {
    if (authStatus === 'authenticated' && !authLoading) {
      const url = getURLPriorToLogin();
      if (url) {
        navigate(url);
      }
    }
  }, [authStatus, authLoading]);

  useEffect(() => {
    if (authStatus === 'unauthenticated' && !authLoading && !djangoLoading) {
      const sessionExpiredEvent = new CustomEvent('sessionExpiredEvent');
      dispatchEvent(sessionExpiredEvent);
    }
  }, [authStatus, authLoading, djangoLoading]);

  const isOnline = useMemo(() => !error && navigator.onLine, [error, navigator.onLine]);

  if (!isOnline) {
    return <OfflineApp isOnline={navigator.onLine} />;
  }
  if (authStatus === 'authenticated') {
    return (
      <>
        <SessionExpiredDialog />
        <ProtectedApp />
      </>
    );
  }
  if (authStatus === 'unauthenticated' && !authLoading && !djangoLoading) {
    return <AppAuth />;
  }
  return <Loading />;
}

export default App;
